import { useState } from "react";
import { createPortal } from "react-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clsx } from "clsx";
import { Ban, Download, FilePlus2, Replace, X } from "lucide-react";
import apiClient from "@/api/client";
import { formatAmount } from "@/utils/formatAmount";
import { triggerBrowserDownload } from "@/utils/download";
import { DatePicker } from "@/components/ui/DatePicker";
import { FileDropZone } from "@/components/ui/FileDropZone";
import {
  CONTRACT_DOCUMENT_KIND_LABELS,
  type Project,
  type ProjectContractDocument,
  type ProjectContractDocumentKind,
} from "../types";

const KINDS: ProjectContractDocumentKind[] = [
  "annex_term",
  "annex_amount",
  "annex_other",
  "termination",
  "settlement",
];

const KIND_HINTS: Record<ProjectContractDocumentKind, string> = {
  annex_term: "Skan aneksu + nowy termin obowiązywania umowy. Termin przedsięwzięcia zmieni się automatycznie.",
  annex_amount: "Skan aneksu + nowa wartość umowy. Wartość przedsięwzięcia zmieni się automatycznie.",
  annex_other: "Skan aneksu + ręczny opis, co się zmienia.",
  termination: "Skan wypowiedzenia. Umowa dostanie znacznik „w trakcie wypowiedzenia” tu i na projekcie ZE.",
  settlement: "Skan porozumienia — np. w sprawie wypowiedzenia (wtedy można oznaczyć umowę jako wypowiedzianą) albo w innej kwestii.",
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}

function fmtDateTime(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? iso : d.toLocaleDateString("pl-PL");
}

type FieldErrors = Partial<Record<string, string[] | string>>;

/** Komunikat z nieudanego zapisu (koperta: { errors: { detail | <pole>[] } }). */
function uploadErrors(err: unknown): FieldErrors {
  const errs = (err as { response?: { data?: { errors?: FieldErrors } } })?.response?.data?.errors;
  return errs ?? { detail: "Nie udało się wgrać dokumentu." };
}

function firstError(errors: FieldErrors, field: string): string | null {
  const v = errors[field];
  if (!v) return null;
  return Array.isArray(v) ? v[0] ?? null : v;
}

interface Props {
  project: Project;
  /** Wgrywanie: dyrektor techniczny albo zarząd (ten sam krąg, co dla umowy). */
  canManage: boolean;
  /** Pokazuje kwoty w wierszach aneksów kwotowych (prawo do kwot przedsięwzięcia). */
  showAmounts: boolean;
}

/**
 * Aneksy, wypowiedzenie i porozumienie do umowy przedsięwzięcia. Lista czyta
 * GET /projects/{id}/contract-documents/; wgranie (POST, multipart) nanosi skutek
 * dokumentu na przedsięwzięcie po stronie backendu i zakłada notatkę w konwersacji
 * dyrektorskiej — stąd po zapisie unieważniamy przedsięwzięcie i wiadomości.
 * Pomyłkowy wpis się wycofuje (…/withdraw/): zostaje na liście jako nieaktualny.
 */
export function ContractDocumentsSection({ project, canManage, showAmounts }: Props) {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [replacing, setReplacing] = useState<ProjectContractDocument | null>(null);
  const [withdrawing, setWithdrawing] = useState<ProjectContractDocument | null>(null);
  // Co backend zrobił ze skutkiem wycofanego dokumentu (meta.effect) — pokazujemy raz.
  const [withdrawEffect, setWithdrawEffect] = useState<string | null>(null);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["project-contract-documents", project.id],
    queryFn: () =>
      apiClient
        .get<{ data: ProjectContractDocument[] }>(`/projects/${project.id}/contract-documents/`)
        .then((r) => r.data.data),
  });

  const download = useMutation({
    mutationFn: (docId: string) =>
      apiClient
        .get<{ data: { url: string; filename: string } }>(
          `/projects/${project.id}/contract-documents/${docId}/download/`,
        )
        .then((r) => r.data.data),
    onSuccess: ({ url, filename }) => triggerBrowserDownload(url, filename),
  });

  if (!canManage && !isLoading && docs.length === 0) return null;

  return (
    <div className="mb-8" data-testid="contract-documents-section">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Aneksy i dokumenty umowy</h2>
        {canManage && (
          <button
            type="button"
            onClick={() => setModal(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            title="Aneks (terminowy / kwotowy / inny), wypowiedzenie albo porozumienie"
          >
            <FilePlus2 size={14} /> Dodaj dokument umowy
          </button>
        )}
      </div>
      {withdrawEffect && (
        <div
          role="status"
          className="mb-2 flex items-start justify-between gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 px-3 py-2 text-sm text-gray-700 dark:text-gray-300"
        >
          <span>Dokument wycofany. {withdrawEffect}</span>
          <button
            type="button"
            onClick={() => setWithdrawEffect(null)}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label="Zamknij"
          >
            <X size={14} />
          </button>
        </div>
      )}
      {docs.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500">
          {isLoading ? "Ładowanie…" : "Brak aneksów, wypowiedzenia ani porozumienia."}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/60 text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2 text-left">Rodzaj</th>
                <th className="px-4 py-2 text-left">Skutek</th>
                <th className="px-4 py-2 text-left">Dodano</th>
                <th className="px-4 py-2 text-right">Plik</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {docs.map((d) => (
                <tr key={d.id} className={clsx(d.is_withdrawn && "bg-gray-50/60 dark:bg-gray-800/30")}>
                  <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-800 dark:text-gray-200">
                    <span className={clsx(d.is_withdrawn && "line-through text-gray-400 dark:text-gray-500")}>
                      {d.kind_display}
                    </span>
                    {d.is_withdrawn && (
                      <span className="ml-2 inline-block rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                        Nieaktualny
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    <div className={clsx(d.is_withdrawn && "line-through text-gray-400 dark:text-gray-500")}>
                      <DocumentEffect doc={d} showAmounts={showAmounts} />
                    </div>
                    {d.is_withdrawn && (
                      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                        Wycofany {d.withdrawn_at ? fmtDateTime(d.withdrawn_at) : ""}
                        {d.withdrawn_by_name && ` · ${d.withdrawn_by_name}`}
                        {d.withdrawal_reason && ` — ${d.withdrawal_reason}`}
                      </p>
                    )}
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap text-gray-500 dark:text-gray-400">
                    {fmtDateTime(d.created_at)}
                    {d.created_by_name && <span className="ml-1">· {d.created_by_name}</span>}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    {d.filename ? (
                      <button
                        type="button"
                        onClick={() => download.mutate(d.id)}
                        disabled={download.isPending}
                        className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline disabled:opacity-50"
                        title={d.filename}
                      >
                        <Download size={13} /> Pobierz
                      </button>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                    {canManage && !d.is_withdrawn && (
                      <>
                        <button
                          type="button"
                          onClick={() => setReplacing(d)}
                          className="ml-3 inline-flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 hover:underline"
                          title="Podmień skan — termin, wartość i skutek dokumentu zostają bez zmian"
                        >
                          <Replace size={13} /> Podmień
                        </button>
                        <button
                          type="button"
                          onClick={() => setWithdrawing(d)}
                          className="ml-3 inline-flex items-center gap-1 text-gray-500 dark:text-gray-400 hover:text-red-700 dark:hover:text-red-400 hover:underline"
                          title="Oznacz pomyłkowy wpis jako nieaktualny — zostanie w historii"
                        >
                          <Ban size={13} /> Wycofaj
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {download.isError && (
        <p className="text-xs text-red-600 mt-1">
          {firstError(uploadErrors(download.error), "detail") ?? "Nie udało się pobrać pliku."}
        </p>
      )}
      {replacing && (
        <ReplaceFileModal
          project={project}
          doc={replacing}
          onClose={() => setReplacing(null)}
          onSaved={() => {
            setReplacing(null);
            qc.invalidateQueries({ queryKey: ["project-contract-documents", project.id] });
            qc.invalidateQueries({ queryKey: ["project-messages"] });
          }}
        />
      )}
      {withdrawing && (
        <WithdrawModal
          project={project}
          doc={withdrawing}
          onClose={() => setWithdrawing(null)}
          onSaved={(effect) => {
            setWithdrawing(null);
            setWithdrawEffect(effect);
            qc.invalidateQueries({ queryKey: ["project-contract-documents", project.id] });
            qc.invalidateQueries({ queryKey: ["projects", project.id] });
            qc.invalidateQueries({ queryKey: ["projects"] });
            qc.invalidateQueries({ queryKey: ["project-messages"] });
            qc.invalidateQueries({ queryKey: ["pp"] });
          }}
        />
      )}
      {modal && (
        <ContractDocumentModal
          project={project}
          showAmounts={showAmounts}
          onClose={() => setModal(false)}
          onSaved={() => {
            setModal(false);
            qc.invalidateQueries({ queryKey: ["project-contract-documents", project.id] });
            qc.invalidateQueries({ queryKey: ["projects", project.id] });
            qc.invalidateQueries({ queryKey: ["projects"] });
            qc.invalidateQueries({ queryKey: ["project-messages"] });
            // Nagłówek projektu ZE pokazuje ten sam znacznik losu umowy.
            qc.invalidateQueries({ queryKey: ["pp"] });
          }}
        />
      )}
    </div>
  );
}

function DocumentEffect({ doc, showAmounts }: { doc: ProjectContractDocument; showAmounts: boolean }) {
  let effect: React.ReactNode = null;
  if (doc.kind === "annex_term") {
    effect = (
      <>
        Termin: {fmtDate(doc.previous_end_date)} → <strong>{fmtDate(doc.new_end_date)}</strong>
      </>
    );
  } else if (doc.kind === "annex_amount") {
    effect = showAmounts ? (
      <>
        Wartość: {doc.previous_total_value ? `${formatAmount(doc.previous_total_value)} PLN` : "—"} →{" "}
        <strong>{doc.new_total_value ? `${formatAmount(doc.new_total_value)} PLN` : "—"}</strong>
      </>
    ) : (
      <span className="text-gray-400">Zmiana wartości umowy</span>
    );
  } else if (doc.kind === "termination") {
    effect = <span className="text-orange-700 dark:text-orange-400">Umowa w trakcie wypowiedzenia</span>;
  } else if (doc.kind === "settlement" && doc.marks_terminated) {
    effect = (
      <span className="text-red-700 dark:text-red-400">
        Umowa wypowiedziana — {fmtDate(doc.effective_date)}
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-0.5">
      {effect}
      {doc.description && (
        <span className={clsx("text-gray-600 dark:text-gray-400 whitespace-pre-line", effect && "text-xs")}>
          {doc.description}
        </span>
      )}
      {!effect && !doc.description && <span className="text-gray-400">—</span>}
    </div>
  );
}

function ContractDocumentModal({
  project,
  showAmounts,
  onClose,
  onSaved,
}: {
  project: Project;
  showAmounts: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [kind, setKind] = useState<ProjectContractDocumentKind>("annex_term");
  const [files, setFiles] = useState<File[]>([]);
  const [newEndDate, setNewEndDate] = useState("");
  const [newTotalValue, setNewTotalValue] = useState("");
  const [description, setDescription] = useState("");
  const [marksTerminated, setMarksTerminated] = useState(false);
  const [effectiveDate, setEffectiveDate] = useState("");

  const terminating = project.contract_status === "terminating";
  const terminated = project.contract_status === "terminated";

  const upload = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append("kind", kind);
      form.append("file", files[0]);
      if (description.trim()) form.append("description", description.trim());
      if (kind === "annex_term" && newEndDate) form.append("new_end_date", newEndDate);
      if (kind === "annex_amount" && newTotalValue) {
        form.append("new_total_value", newTotalValue.replace(/\s/g, "").replace(",", "."));
      }
      if (kind === "settlement") {
        form.append("marks_terminated", marksTerminated ? "true" : "false");
        if (marksTerminated && effectiveDate) form.append("effective_date", effectiveDate);
      }
      return apiClient.post(`/projects/${project.id}/contract-documents/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
    },
    onSuccess: onSaved,
  });

  const errors: FieldErrors = upload.isError ? uploadErrors(upload.error) : {};

  // Blokada przycisku odzwierciedla walidację backendu — żeby 400 było wyjątkiem,
  // nie zwykłą drogą.
  const ready =
    !!files[0] &&
    (kind !== "annex_term" || !!newEndDate) &&
    (kind !== "annex_amount" || !!newTotalValue) &&
    (kind !== "annex_other" || !!description.trim()) &&
    (kind !== "settlement" || (marksTerminated ? !!effectiveDate : !!description.trim()));

  const descriptionRequired = kind === "annex_other" || (kind === "settlement" && !marksTerminated);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg p-6 flex flex-col gap-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">Dodaj dokument umowy</h2>

        <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Rodzaj dokumentu">
          {KINDS.map((k) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={kind === k}
              onClick={() => { setKind(k); upload.reset(); }}
              disabled={upload.isPending || (k === "termination" && terminated)}
              title={k === "termination" && terminated ? "Umowa jest już wypowiedziana" : undefined}
              className={clsx(
                "px-3 py-1.5 rounded-lg border text-sm transition-colors disabled:opacity-40",
                kind === k
                  ? "border-[var(--color-primary)] bg-[var(--color-primary)] text-white"
                  : "border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50",
              )}
            >
              {CONTRACT_DOCUMENT_KIND_LABELS[k]}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">{KIND_HINTS[kind]}</p>

        <FileDropZone
          files={files}
          onFilesChange={setFiles}
          accept=".pdf,.jpg,.jpeg,.png"
          disabled={upload.isPending}
          label="Przeciągnij skan (PDF) albo kliknij, żeby wybrać"
        />
        {firstError(errors, "file") && <p className="text-xs text-red-600 -mt-2">{firstError(errors, "file")}</p>}

        {kind === "annex_term" && (
          <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
            <span>
              Nowy termin obowiązywania umowy
              {project.planned_end_date && (
                <span className="text-gray-400 dark:text-gray-500"> (obecnie {fmtDate(project.planned_end_date)})</span>
              )}
            </span>
            <DatePicker
              value={newEndDate}
              onChange={setNewEndDate}
              disabled={upload.isPending}
              aria-label="Nowy termin obowiązywania umowy"
              className="rounded-lg"
            />
            {firstError(errors, "new_end_date") && <span className="text-xs text-red-600">{firstError(errors, "new_end_date")}</span>}
          </label>
        )}

        {kind === "annex_amount" && (
          <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
            <span>
              Nowa wartość umowy (PLN)
              {showAmounts && project.total_value && (
                <span className="text-gray-400 dark:text-gray-500"> (obecnie {formatAmount(project.total_value)} PLN)</span>
              )}
            </span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="np. 125000,00"
              value={newTotalValue}
              onChange={(e) => setNewTotalValue(e.target.value)}
              disabled={upload.isPending}
              className="input-base rounded-lg"
            />
            {firstError(errors, "new_total_value") && <span className="text-xs text-red-600">{firstError(errors, "new_total_value")}</span>}
          </label>
        )}

        {kind === "settlement" && (
          <div className="flex flex-col gap-2">
            <label
              className={clsx("flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300", !terminating && "opacity-60")}
              title={terminating ? undefined : "Najpierw wgraj wypowiedzenie umowy."}
            >
              <input
                type="checkbox"
                checked={marksTerminated}
                onChange={(e) => setMarksTerminated(e.target.checked)}
                disabled={upload.isPending || !terminating}
                className="mt-0.5"
              />
              <span>
                Oznacz umowę jako wypowiedzianą
                {!terminating && (
                  <span className="block text-xs text-gray-400 dark:text-gray-500">
                    {terminated ? "Umowa jest już oznaczona jako wypowiedziana." : "Dostępne po wgraniu wypowiedzenia."}
                  </span>
                )}
              </span>
            </label>
            {firstError(errors, "marks_terminated") && <span className="text-xs text-red-600">{firstError(errors, "marks_terminated")}</span>}
            {marksTerminated && (
              <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
                Data zawarcia porozumienia / uskutecznienia wypowiedzenia
                <DatePicker
                  value={effectiveDate}
                  onChange={setEffectiveDate}
                  disabled={upload.isPending}
                  aria-label="Data zawarcia porozumienia / uskutecznienia wypowiedzenia"
                  className="rounded-lg"
                />
                {firstError(errors, "effective_date") && <span className="text-xs text-red-600">{firstError(errors, "effective_date")}</span>}
              </label>
            )}
          </div>
        )}

        <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
          <span>
            {kind === "annex_other"
              ? "Co zmienia aneks"
              : kind === "settlement"
                ? marksTerminated ? "Uwagi (opcjonalnie)" : "Czego dotyczy porozumienie"
                : "Uwagi (opcjonalnie)"}
            {descriptionRequired && <span className="text-red-500"> *</span>}
          </span>
          <textarea
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={upload.isPending}
            className="input-base rounded-lg resize-y"
          />
          {firstError(errors, "description") && <span className="text-xs text-red-600">{firstError(errors, "description")}</span>}
        </label>

        <p className="text-xs text-gray-500 dark:text-gray-400">
          Po zapisie w konwersacji dyrektorskiej pojawi się automatyczna notatka ze skanem w załączniku.
        </p>
        {(firstError(errors, "detail") || firstError(errors, "kind")) && (
          <p className="text-xs text-red-600">{firstError(errors, "detail") ?? firstError(errors, "kind")}</p>
        )}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={upload.isPending}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-40"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => ready && upload.mutate()}
            disabled={!ready || upload.isPending}
            className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {upload.isPending ? "Wgrywanie…" : "Zapisz dokument"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Podmiana skanu istniejącego dokumentu (POST …/contract-documents/{id}/replace-file/).
 * Zmienia wyłącznie plik — skutek dokumentu zostaje; stary skan backend dokleja do
 * notatki w konwersacji dyrektorskiej razem z nowym.
 */
function ReplaceFileModal({
  project,
  doc,
  onClose,
  onSaved,
}: {
  project: Project;
  doc: ProjectContractDocument;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [files, setFiles] = useState<File[]>([]);
  const [reason, setReason] = useState("");

  const replace = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append("file", files[0]);
      if (reason.trim()) form.append("reason", reason.trim());
      return apiClient.post(
        `/projects/${project.id}/contract-documents/${doc.id}/replace-file/`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
    },
    onSuccess: onSaved,
  });

  const errors: FieldErrors = replace.isError ? uploadErrors(replace.error) : {};

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg p-6 flex flex-col gap-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Podmień skan: {doc.kind_display} z {fmtDateTime(doc.created_at)}
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
          Zmienia się tylko plik{doc.filename ? ` (obecnie: ${doc.filename})` : ""}. Termin, wartość i skutek
          dokumentu zostają bez zmian. Poprzedni skan nie zostanie usunięty — trafi razem z nowym do
          notatki w konwersacji dyrektorskiej.
        </p>

        <FileDropZone
          files={files}
          onFilesChange={setFiles}
          accept=".pdf,.jpg,.jpeg,.png"
          disabled={replace.isPending}
          label="Przeciągnij nowy skan (PDF) albo kliknij, żeby wybrać"
        />
        {firstError(errors, "file") && <p className="text-xs text-red-600 -mt-2">{firstError(errors, "file")}</p>}

        <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
          Powód podmiany (opcjonalnie)
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={replace.isPending}
            placeholder="np. wgrano wersję bez podpisów"
            className="input-base rounded-lg"
          />
        </label>

        {firstError(errors, "detail") && <p className="text-xs text-red-600">{firstError(errors, "detail")}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={replace.isPending}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-40"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => files[0] && replace.mutate()}
            disabled={!files[0] || replace.isPending}
            className="px-4 py-2 text-sm bg-[var(--color-primary)] text-white rounded-lg hover:bg-[var(--color-primary-hover)] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {replace.isPending ? "Wgrywanie…" : "Podmień plik"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * Wycofanie pomyłkowego dokumentu (POST …/contract-documents/{id}/withdraw/). Wpis
 * zostaje w historii jako nieaktualny; termin/wartość/status wracają do stanu sprzed
 * dokumentu tylko wtedy, gdy nic nowszego ich nie zmieniło — co się stało, mówi backend.
 */
function WithdrawModal({
  project,
  doc,
  onClose,
  onSaved,
}: {
  project: Project;
  doc: ProjectContractDocument;
  onClose: () => void;
  onSaved: (effect: string) => void;
}) {
  const [reason, setReason] = useState("");

  const withdraw = useMutation({
    mutationFn: () =>
      apiClient
        .post<{ meta: { effect: string } }>(
          `/projects/${project.id}/contract-documents/${doc.id}/withdraw/`,
          { reason: reason.trim() },
        )
        .then((r) => r.data.meta.effect),
    onSuccess: onSaved,
  });

  const errors: FieldErrors = withdraw.isError ? uploadErrors(withdraw.error) : {};
  const hasEffect =
    doc.kind === "annex_term" ||
    doc.kind === "annex_amount" ||
    doc.kind === "termination" ||
    (doc.kind === "settlement" && doc.marks_terminated);

  return createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg p-6 flex flex-col gap-4 max-h-[90vh] overflow-y-auto">
        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
          Wycofaj: {doc.kind_display} z {fmtDateTime(doc.created_at)}
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 -mt-2">
          Wpis nie zniknie — zostanie w historii jako nieaktualny, razem ze skanem
          {doc.filename ? ` (${doc.filename})` : ""} i powodem wycofania.
          {hasEffect &&
            " Termin, wartość albo status umowy wrócą do stanu sprzed tego dokumentu — chyba że od tamtej pory zmienił je nowszy dokument albo ręczna edycja."}{" "}
          Wycofania nie da się cofnąć; właściwy dokument wgraj potem jako nowy wpis.
        </p>

        <label className="flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
          <span>
            Powód wycofania <span className="text-red-500">*</span>
          </span>
          <textarea
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            disabled={withdraw.isPending}
            placeholder="np. plik z innego zlecenia, aneks wgrany do złego przedsięwzięcia"
            className="input-base rounded-lg resize-y"
          />
          {firstError(errors, "reason") && <span className="text-xs text-red-600">{firstError(errors, "reason")}</span>}
        </label>

        {firstError(errors, "detail") && <p className="text-xs text-red-600">{firstError(errors, "detail")}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={withdraw.isPending}
            className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 disabled:opacity-40"
          >
            Anuluj
          </button>
          <button
            type="button"
            onClick={() => reason.trim() && withdraw.mutate()}
            disabled={!reason.trim() || withdraw.isPending}
            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {withdraw.isPending ? "Wycofywanie…" : "Wycofaj dokument"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
